import React, { useState } from 'react';
import { CalendarIcon, CloudIcon, ThermometerIcon, UmbrellaIcon, SunIcon, InfoIcon } from 'lucide-react';
import { getDestinationWeather } from '../../data/weatherData';
interface WeatherInsightsProps {
  destination: string;
}
export const WeatherInsights: React.FC<WeatherInsightsProps> = ({
  destination
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const weatherData = getDestinationWeather(destination);
  if (!weatherData) {
    return <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center" data-id="element-4230">
        <InfoIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-4231" />
        <p className="text-gray-600" data-id="element-4232">
          Weather data not available for this destination
        </p>
      </div>;
  }
  // Get current month as default selection
  const currentMonth = new Date().toLocaleString('default', {
    month: 'short'
  });
  const effectiveMonth = selectedMonth || currentMonth;
  const monthData = weatherData.months.find(m => m.month === effectiveMonth);
  return <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4233">
      <div className="bg-blue-50 border-b border-blue-100 px-4 py-3 flex items-center" data-id="element-4234">
        <ThermometerIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-4235" />
        <h3 className="font-medium text-gray-800" data-id="element-4236">Weather Insights</h3>
      </div>
      <div className="p-4" data-id="element-4237">
        {/* Month selector */}
        <div className="flex flex-wrap gap-2 mb-4" data-id="element-4238">
          {weatherData.months.map(month => <button key={month.month} onClick={() => setSelectedMonth(month.month)} className={`px-3 py-1 text-xs rounded-full transition-colors ${month.month === (selectedMonth || currentMonth) ? 'bg-blue-100 text-blue-800 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-4239">
              {month.month}
            </button>)}
        </div>
        {/* Selected month details */}
        {monthData && <div className="space-y-4" data-id="element-4240">
            <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3" data-id="element-4241">
              <div className="flex items-center" data-id="element-4242">
                <div className="p-2 bg-white rounded-full mr-3" data-id="element-4243">
                  {monthData.icon}
                </div>
                <div data-id="element-4244">
                  <h4 className="font-medium text-gray-800" data-id="element-4245">
                    {effectiveMonth} in {destination}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-4246">
                    {monthData.season} Season
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-4247">
                <div className="text-lg font-bold text-blue-600" data-id="element-4248">
                  {monthData.tempRange}
                </div>
                <div className="text-xs text-gray-500" data-id="element-4249">Temperature Range</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-4250">
              <div className="border border-gray-200 rounded-lg p-3" data-id="element-4251">
                <div className="flex items-center mb-1" data-id="element-4252">
                  <UmbrellaIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-4253" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-4254">
                    Rainfall
                  </span>
                </div>
                <div className="flex items-center" data-id="element-4255">
                  <div className={`w-full h-2 rounded-full ${monthData.rainfall === 'Very High' ? 'bg-blue-600' : monthData.rainfall === 'High' ? 'bg-blue-400' : monthData.rainfall === 'Moderate' ? 'bg-blue-300' : monthData.rainfall === 'Low' ? 'bg-blue-200' : 'bg-blue-100'}`} data-id="element-4256"></div>
                  <span className="ml-2 text-xs text-gray-600" data-id="element-4257">
                    {monthData.rainfall}
                  </span>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3" data-id="element-4258">
                <div className="flex items-center mb-1" data-id="element-4259">
                  <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-4260" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-4261">
                    Travel Recommendation
                  </span>
                </div>
                <div className="text-sm" data-id="element-4262">
                  {weatherData.bestTimeToVisit.includes(monthData.month) ? <span className="text-green-600 font-medium" data-id="element-4263">
                      Recommended time to visit
                    </span> : <span className="text-gray-600" data-id="element-4264">
                      Consider other months for ideal conditions
                    </span>}
                </div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3" data-id="element-4265">
              <div className="flex items-center mb-2" data-id="element-4266">
                <InfoIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-4267" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4268">
                  Weather Details
                </span>
              </div>
              <p className="text-sm text-gray-600" data-id="element-4269">{monthData.description}</p>
            </div>
            {/* Special events for this month */}
            {weatherData.specialEvents.filter(event => event.month.includes(monthData.month) || event.month === 'Year-round').map((event, index) => <div key={index} className="border border-amber-200 bg-amber-50 rounded-lg p-3" data-id="element-4270">
                  <div className="flex items-center mb-1" data-id="element-4271">
                    <CalendarIcon className="h-4 w-4 text-amber-500 mr-1" data-id="element-4272" />
                    <span className="text-sm font-medium text-amber-800" data-id="element-4273">
                      {event.name}
                    </span>
                  </div>
                  <p className="text-sm text-amber-700" data-id="element-4274">{event.description}</p>
                </div>)}
          </div>}
        {/* Best time to visit */}
        <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-4275">
          <h4 className="font-medium text-gray-800 mb-2" data-id="element-4276">Best Time to Visit</h4>
          <div className="flex flex-wrap gap-2" data-id="element-4277">
            {weatherData.bestTimeToVisit.map(month => <div key={month} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full flex items-center" data-id="element-4278">
                <SunIcon className="h-3 w-3 mr-1" data-id="element-4279" />
                {month}
              </div>)}
          </div>
          <p className="text-sm text-gray-600 mt-2" data-id="element-4280">
            These months typically offer the most favorable weather conditions
            for visiting {destination}.
          </p>
        </div>
      </div>
    </div>;
};